import HTTP from '@/utils/http'
import notification from '@/utils/notification'
import i18n from '@/utils/i18n'
import * as revisionRequest from '@/api/tender-addendums/request'
import { vendorBidStatus, tenderStatus, queryStatus, queryStatusMap } from '@/utils/locale'
import * as printer from '@/api/printers/request'
import lodash from 'lodash'

export function parseVendorBidStatus (tender, bid) {
  const tenderGeneral = tender.general ? tender.general : tender
  if (bid && bid.status) {
    if ((tenderGeneral.version > 1 || (tenderGeneral.version === 1 && tenderGeneral.status === tenderStatus.ISSUED)) && bid.status === vendorBidStatus.INVITED) {
      return 'A-' + bid.status
    }
    if (tenderGeneral.status === tenderStatus.REVIEW && bid.status === vendorBidStatus.SUBMITTED && bid.queries && bid.queries.length > 0) {
      const lastQuery = getLatestQuery(bid)
      if (lastQuery.status === queryStatus.ISSUED) return queryStatusMap.ISSUED
      if (isQuerySubmitted(lastQuery)) return queryStatusMap.SUBMITTED
    }
    return bid.status
  }
  return ''
}

function getLatestQuery (bid) {
  const lastQuery = bid.queries.reduce((last, query) => {
    return last && new Date(last.createdAt).getTime() > new Date(query.createdAt).getTime() ? last : query
  }, {})
  return lastQuery
}

function isQuerySubmitted (query = {}) {
  return query.status === queryStatus.SUBMITTED && new Date(query.closingDate).getTime() > Date.now()
}

export async function getQtyAddendumedServiceLineItemsFromCurTender (tenderId, currentTender) {
  if (!tenderId || !currentTender) return null
  const lastVerServiceSecs = deep((await revisionRequest.getRevisionByVersionNum(tenderId)), 'service.data') || []
  const lastVerTenderDict = {}
  lastVerServiceSecs.forEach(section => section.lineItems.map(lineItem => {
    lastVerTenderDict[lineItem.id] = lineItem
  }))
  const currentServiceSecs = deep(currentTender, 'service') || []
  const curTenderDict = {}
  currentServiceSecs.forEach(section => section.lineItems.map(lineItem => {
    curTenderDict[lineItem.id] = lineItem
  }))
  const qtyUpdatedLineItems = []
  Object.keys(curTenderDict).forEach(id => {
    if (id in lastVerTenderDict && (Number(lastVerTenderDict[id].itemQty) !== Number(curTenderDict[id].itemQty))) {
      qtyUpdatedLineItems.push(curTenderDict[id])
    }
  })
  return qtyUpdatedLineItems
}

function sortQueries (response) {
  if (!response || !response.bid || !response.bid.queries || response.bid.queries.length === 0) return response
  const bid = response.bid
  const queries = lodash.orderBy(bid.queries, 'order').reverse()
  return { ...response, bid: { ...bid, queries } }
}

export async function getBids () {
  try {
    const response = await HTTP.get('/bids')
    return response.data.data
  } catch (err) {
    return []
  }
}

export async function getBidById (bidId) {
  try {
    const response = await HTTP.get(`bids/${bidId}`)
    return sortQueries(response.data.data)
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getBidsByTenderId (tenderId) {
  try {
    const response = await HTTP.get(`/bids/tender/${tenderId}`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateBid (request, bidId) {
  const response = await HTTP.put(`/bids/${bidId}`, request)
  return response.data.data
}

export async function updateBidStatus (bidId, request, action) {
  try {
    const response = await HTTP.patch(`/bids/${bidId}`, request, { headers: { 'X-CMS-Action': action } })
    notification.handleResponseAlert(response)
  } catch (err) {
    notification.handleResponseError(err, 'bids')
    throw err
  }
}

export async function resetBidsToDraft (tenderId, currentTender) {
  try {
    const qtyUpdatedLineIds = ((await getQtyAddendumedServiceLineItemsFromCurTender(tenderId, currentTender)) || []).map(rec => rec.id)
    const vendorBids = await getBidsByTenderId(tenderId)
    const promiseUpdates = []
    vendorBids.forEach((vendorBid) => {
      if (!['INVITED', 'DECLINED'].includes(vendorBid.status)) {
        vendorBid.status = 'INVITED'
      }
      deep(vendorBid, 'content.tncInfo', { name: '', position: '' })
      const curQtys = (deep(vendorBid, 'content.service.bidItemQtys') || {})
      deep(vendorBid, 'content.service.bidItemQtys', Object.keys(curQtys).reduce((result, id) => {
        if (!qtyUpdatedLineIds.includes(id)) {
          result[id] = curQtys[id]
        }
        return result
      }, {}))
      promiseUpdates.push(updateBid(vendorBid, vendorBid.id))
    })
    await Promise.all(promiseUpdates)
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function exportContractorPDF (type, request, bidService) {
  const exportOptions = {
    bid: `${i18n.t('bid')} ${i18n.t('summary.name')}`,
    query: `${i18n.t('tender.query.name')} ${i18n.t('summary.name')}`,
  }

  switch (type) {
    case exportOptions.bid:
      await printer.exportTenderBid(request, bidService)
      break
    case exportOptions.query:
      await printer.exportQuery(request)
      break
    default: // Do nothing
  }
}
